/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormEvent, ReactNode, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Column, type RowColumnProps } from 'components/Blocks/Containers';

import { InferSchema, PossibleSchema } from 'utils/validation';

import { AnyObject } from 'types';
import { resolveDefaultValues } from './schema';

export interface FormWrapperProps<
  T extends PossibleSchema,
  __VALUE__ extends AnyObject = InferSchema<NoInfer<T>>,
> extends Omit<RowColumnProps, 'children' | 'ref' | 'onSubmit' | 'as'> {
  schema: T;
  defaultValues?: Partial<__VALUE__>;
  mode?: 'onBlur' | 'onChange' | 'onSubmit';
  children: ReactNode;
  onSubmit?: (data: __VALUE__) => unknown | Promise<unknown>;
  onBeforeSubmit?: () => void;
}

export function FormWrapper<T extends PossibleSchema>({
  schema,
  defaultValues,
  mode = 'onSubmit',
  children,
  onSubmit,
  onBeforeSubmit,
  ...props
}: FormWrapperProps<T>) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaults = useMemo(() => resolveDefaultValues(schema, defaultValues), []);

  const form = useForm({
    resolver: zodResolver(schema),

    // We are strictly typing our values with the schema + type
    defaultValues: defaults as any,

    mode,
    reValidateMode: mode,
  });

  const formProps = onSubmit
    ? {
        as: 'form' as const,
        onSubmit: (e: FormEvent) => {
          onBeforeSubmit?.();
          return form.handleSubmit(onSubmit)(e);
        },
      }
    : {};

  return (
    <FormProvider {...form}>
      <Column {...(formProps as RowColumnProps)} {...props}>
        {/*  */}
        {children}
      </Column>
    </FormProvider>
  );
}
